import React from 'react';
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import { FaArrowRight, FaCheck } from 'react-icons/fa';
import Footer from './Footer';
import Seo from './Seo';
import SkullIcon from './SkullIcon';
import { SITE_URL, SITE_NAME } from '../constants/site';
import { getIdea, getBlend } from '../data/tattooIdeas';

const STEPS = [
  { n: '01', t: 'Add your design', d: 'Generate one with AI, upload your own art, or add text.' },
  { n: '02', t: 'Place it on the 3D model', d: 'Drop it onto any of 50+ body placements and adjust the fit.' },
  { n: '03', t: 'Preview from every angle', d: 'Rotate to see how it really looks, then save or share it.' },
];

export default function TattooIdeasPage() {
  const { slug, placementSlug } = useParams();
  const navigate = useNavigate();

  const idea = placementSlug ? getBlend(slug, placementSlug) : getIdea(slug);

  // Unknown slug → send to the tool rather than 404.
  if (!idea) return <Navigate to="/" replace />;

  const goCreate = () => navigate('/');

  // Breadcrumb chain (Home → [parent] → current), used for both the visible
  // trail and the BreadcrumbList structured data.
  const crumbs = [{ name: 'Home', to: '/' }];
  if (idea.parent) crumbs.push({ name: idea.parent.label, to: idea.parent.to });
  crumbs.push({ name: idea.crumbLabel, to: idea.path });

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: c.name,
        item: `${SITE_URL}${c.to}`,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: idea.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-800 text-white antialiased">
      <Seo title={idea.title} description={idea.description} path={idea.path} jsonLd={jsonLd} />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-gray-800/70 backdrop-blur-xl">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <SkullIcon size={26} className="translate-y-[2px] text-rose-500" />
            <span className="text-lg font-semibold tracking-tight">{SITE_NAME}</span>
          </Link>
          <button
            onClick={goCreate}
            className="rounded-full bg-gradient-to-r from-rose-500 to-pink-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-rose-500/20 transition hover:brightness-110"
          >
            Start free
          </button>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-[-10%] h-[520px] w-[720px] rounded-full bg-rose-600/15 blur-[150px]"
        />
        <div className="relative mx-auto max-w-6xl px-6 pb-14 pt-12">
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm text-white/40" aria-label="Breadcrumb">
            {crumbs.map((c, i) => {
              const last = i === crumbs.length - 1;
              return (
                <span key={c.to}>
                  {last ? (
                    <span className="text-white/70">{c.name}</span>
                  ) : (
                    <Link to={c.to} className="transition hover:text-white/70">
                      {c.name}
                    </Link>
                  )}
                  {!last && <span className="mx-2">/</span>}
                </span>
              );
            })}
          </nav>

          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h1 className="text-4xl font-semibold leading-[1.08] tracking-tight md:text-5xl">
                {idea.h1}
              </h1>
              <p className="mt-5 max-w-xl text-lg text-white/60">{idea.intro}</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button
                  onClick={goCreate}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-rose-500 to-pink-600 px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-rose-500/25 transition hover:brightness-110"
                >
                  Preview {idea.name.toLowerCase()} tattoos on your body
                  <FaArrowRight className="text-sm" />
                </button>
              </div>
              <p className="mt-4 text-sm text-white/40">
                Free to start · works in your browser
              </p>
            </div>

            <div className="relative">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 -z-10 translate-x-6 translate-y-6 rounded-2xl bg-rose-600/20 blur-2xl"
              />
              <img
                src="/assets/hero-preview.jpg"
                alt={`Preview a ${idea.name.toLowerCase()} tattoo on a realistic 3D body model in KoiCreate`}
                className="block w-full rounded-2xl border border-white/10 shadow-2xl shadow-black/60"
                loading="eager"
                width="1800"
                height="1350"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why preview */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">
            Why preview {idea.name.toLowerCase()} tattoos in 3D?
          </h2>
          <ul className="mt-8 grid gap-4 md:grid-cols-3">
            {idea.points.map((p, i) => (
              <li
                key={i}
                className="flex gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-white/70"
              >
                <FaCheck className="mt-1 shrink-0 text-emerald-400" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">How it works</h2>
        <div className="mt-8 grid gap-8 md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n}>
              <div className="text-sm font-semibold text-rose-400">{s.n}</div>
              <h3 className="mt-2 text-xl font-semibold">{s.t}</h3>
              <p className="mt-2 text-white/60">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-6 py-16">
        <h2 className="text-center text-2xl font-semibold tracking-tight md:text-3xl">
          {idea.name} tattoo FAQs
        </h2>
        <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
          {idea.faqs.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between text-lg font-medium text-white/90 transition hover:text-white">
                {f.q}
                <span className="ml-4 text-white/40 transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 leading-relaxed text-white/60">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-rose-500/15 via-transparent to-pink-600/15 px-8 py-12 text-center">
          <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">
            See your {idea.name.toLowerCase()} tattoo on your body
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-white/60">
            Design it with AI or upload your own, then preview it in realistic 3D. Free to start.
          </p>
          <div className="mt-7 flex justify-center">
            <button
              onClick={goCreate}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-rose-500 to-pink-600 px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-rose-500/25 transition hover:brightness-110"
            >
              Start creating now <FaArrowRight className="text-sm" />
            </button>
          </div>
        </div>
      </section>

      {/* Related links (internal linking for the style × placement pages) */}
      {idea.related && idea.related.length > 0 && (
        <section className="border-t border-white/10">
          <div className="mx-auto max-w-6xl px-6 py-12">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-white/40">
              {idea.relatedTitle}
            </h2>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {idea.related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-white/70 transition hover:border-white/20 hover:text-white"
                >
                  {r.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
